import { create } from 'zustand'
import { api, ApiRequestError } from '../lib/api'

// ────────────────────────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────────────────────────

export interface VocabTerm {
  id: string
  term: string
  display_term: string | null
  vocabulary_type: string
  definition: string | null
  aliases: string[]
  status: string
  source: string
  created_at: string
  retired_at: string | null
}

export interface VocabCandidate {
  id: string
  proposed_term: string
  normalized_term: string | null
  vocabulary_type: string
  nearest_existing_term: string | null
  similarity_score: number | null
  occurrence_count: number
  raw_forms: string[]
  status: string
  created_at: string
}

export type VocabFilter = 'all' | 'pending' | 'approved'

interface VocabularyState {
  terms: VocabTerm[]
  candidates: VocabCandidate[]
  filter: VocabFilter
  typeFilter: string
  loading: boolean
  acting: string | null
  error: string | null
  autoLinked: VocabCandidate[]
  autoLinkedLoading: boolean
}

interface VocabularyActions {
  load: () => Promise<void>
  setFilter: (f: VocabFilter) => void
  setTypeFilter: (t: string) => void
  approveCandidiate: (id: string) => Promise<void>
  rejectCandidate: (id: string) => Promise<void>
  mergeCandidate: (id: string, targetTermId: string) => Promise<void>
  patchTerm: (id: string, patch: { definition?: string; display_term?: string }) => Promise<void>
  addAlias: (id: string, alias: string) => Promise<void>
  retireTerm: (id: string) => Promise<void>
  mergeTerms: (sourceId: string, intoId: string) => Promise<void>
  loadAutoLinked: () => Promise<void>
  flagAutoLinked: (id: string) => Promise<void>
  pendingCount: () => number
}

// ────────────────────────────────────────────────────────────────────────────
// Store
// ────────────────────────────────────────────────────────────────────────────

const initialState: VocabularyState = {
  terms: [],
  candidates: [],
  filter: 'all',
  typeFilter: '',
  loading: false,
  acting: null,
  error: null,
  autoLinked: [],
  autoLinkedLoading: false,
}

export const useVocabularyStore = create<VocabularyState & VocabularyActions>((set, get) => ({
  ...initialState,

  pendingCount: () => get().candidates.filter((c) => c.status === 'pending').length,

  setFilter: (filter) => set({ filter }),
  setTypeFilter: (typeFilter) => set({ typeFilter }),

  load: async () => {
    set({ loading: true, error: null })
    try {
      const typeFilter = get().typeFilter
      const params = typeFilter ? `?vocabulary_type=${typeFilter}` : ''
      const res = await api.get<{ terms: VocabTerm[]; candidates: VocabCandidate[] }>(
        `/api/v1/admin/vocabulary${params}`,
      )
      set({ terms: res.terms, candidates: res.candidates, loading: false })
    } catch (err) {
      set({
        loading: false,
        error: err instanceof ApiRequestError ? err.error.message : 'Failed to load vocabulary',
      })
    }
  },

  approveCandidiate: async (id) => {
    set({ acting: id })
    try {
      await api.post(`/api/v1/admin/vocabulary/candidates/${id}/approve`)
      await get().load()
    } catch { /* no-op */ } finally {
      set({ acting: null })
    }
  },

  rejectCandidate: async (id) => {
    set({ acting: id })
    try {
      await api.post(`/api/v1/admin/vocabulary/candidates/${id}/reject`)
      set((state) => ({
        candidates: state.candidates.filter((c) => c.id !== id),
        acting: null,
      }))
    } catch { /* no-op */ } finally {
      set({ acting: null })
    }
  },

  mergeCandidate: async (id, targetTermId) => {
    set({ acting: id })
    try {
      await api.post(`/api/v1/admin/vocabulary/candidates/${id}/merge`, {
        target_term_id: targetTermId,
      })
      await get().load()
    } catch { /* no-op */ } finally {
      set({ acting: null })
    }
  },

  patchTerm: async (id, patch) => {
    set({ acting: id })
    try {
      const updated = await api.patch<VocabTerm>(`/api/v1/admin/vocabulary/terms/${id}`, patch)
      set((state) => ({
        terms: state.terms.map((t) => (t.id === id ? updated : t)),
        acting: null,
      }))
    } catch { /* no-op */ } finally {
      set({ acting: null })
    }
  },

  addAlias: async (id, alias) => {
    set({ acting: id })
    try {
      const updated = await api.post<VocabTerm>(`/api/v1/admin/vocabulary/terms/${id}/alias`, {
        alias,
      })
      set((state) => ({
        terms: state.terms.map((t) => (t.id === id ? updated : t)),
        acting: null,
      }))
    } catch { /* no-op */ } finally {
      set({ acting: null })
    }
  },

  retireTerm: async (id) => {
    set({ acting: id })
    try {
      await api.post(`/api/v1/admin/vocabulary/terms/${id}/retire`)
      set((state) => ({
        terms: state.terms.filter((t) => t.id !== id),
        acting: null,
      }))
    } catch { /* no-op */ } finally {
      set({ acting: null })
    }
  },

  mergeTerms: async (sourceId, intoId) => {
    set({ acting: sourceId })
    try {
      await api.post(`/api/v1/admin/vocabulary/terms/${sourceId}/merge`, { into_term_id: intoId })
      await get().load()
    } catch { /* no-op */ } finally {
      set({ acting: null })
    }
  },

  loadAutoLinked: async () => {
    set({ autoLinkedLoading: true })
    try {
      const res = await api.get<{ data: VocabCandidate[]; count: number }>(
        '/api/v1/admin/vocabulary/auto-linked',
      )
      set({ autoLinked: res.data, autoLinkedLoading: false })
    } catch {
      set({ autoLinkedLoading: false })
    }
  },

  flagAutoLinked: async (id) => {
    set({ acting: id })
    try {
      await api.post(`/api/v1/admin/vocabulary/auto-linked/${id}/flag`)
      set((state) => ({
        autoLinked: state.autoLinked.filter((c) => c.id !== id),
        acting: null,
      }))
      // Reload main table so the newly pending row appears there
      await get().load()
    } catch { /* no-op */ } finally {
      set({ acting: null })
    }
  },
}))
